import { Box, HStack, Icon, IconButton, Input, Stack, Text, useColorModeValue, VStack } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { FiEye, FiFile, FiTrash, FiUpload } from 'react-icons/fi';
import React, { DragEvent, useEffect, useMemo, useState } from 'react';
import { createFileCopy, formatFileSize } from '../../../../../utils/functions';
import { IObjectFile } from '../../../../../services/hooks/Registration/useGetRegistration';

interface IOwnProps {
  inputName?: string;
  sentFiles?: IObjectFile[];
  onDeleteAttachment?(id: string): void;
  allowMultiple?: boolean;
  minimumFiles?: number;
  accept?: string;
}

const InputAttachmentFile: React.FC<IOwnProps> = ({
  inputName = 'attachmentFile',
  sentFiles,
  onDeleteAttachment,
  allowMultiple = true,
  accept,
}) => {
  const { formState, watch, setValue, register } = useFormContext();
  const [isDragging, setIsDragging] = useState(false);
  const bgUploadBox = useColorModeValue('blue.50', 'blue.900');
  const bgListFile = useColorModeValue('gray.50', 'gray.800');
  const borderUploadBox = formState.errors[inputName] ? 'red' : useColorModeValue('gray.300', 'gray.600');

  const removeFile = (index: number) => {
    const newFiles = listAttachmentFile.filter((_, i) => i !== index);
    setValue(inputName, newFiles, { shouldValidate: true });
  };

  const attachmentValue = watch(inputName);
  const listAttachmentFile = Object.values<File>(attachmentValue ?? {});
  const previewUrls = useMemo(() => listAttachmentFile.map((file) => URL.createObjectURL(file)), [attachmentValue]);

  useEffect(() => {
    return () => previewUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [previewUrls]);

  const addFiles = async (files: File[]) => {
    if (files.length === 0) return;
    const selectedFiles = files.slice(0, 1);
    const fileCopies = await Promise.all(selectedFiles.map((file) => createFileCopy(file)));
    setValue(inputName, allowMultiple ? [...listAttachmentFile, ...fileCopies] : fileCopies, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    await addFiles(Array.from(event.dataTransfer.files));
  };

  return (
    <Stack>
      <label htmlFor={inputName + 'file-input'}>
        <Box
          border="2px dashed"
          borderColor={borderUploadBox}
          rounded="xl"
          p={6}
          textAlign="center"
          cursor="pointer"
          bg={isDragging ? bgUploadBox : undefined}
          onDragEnter={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          _hover={{ borderColor: 'blue.400', bg: bgUploadBox }}
        >
          <VStack spacing={3}>
            <Icon as={FiUpload} boxSize={8} color="blue.400" />
            <Text fontSize="sm" color="gray.500">
              Arraste e solte um arquivo ou clique para enviar os anexos para sua solicitação
            </Text>
            <Input
              {...register(inputName, {
                onChange: async (file: React.ChangeEvent<HTMLInputElement>) => {
                  file.preventDefault();
                  if (!file.target || file.target.files?.length === 0 || !file.target.files) return;

                  try {
                    await addFiles(Array.from(file.target.files));
                  } finally {
                    file.target.value = '';
                  }
                },
              })}
              id={inputName + 'file-input'}
              type="file"
              accept={accept}
              hidden
            />
          </VStack>
        </Box>
      </label>
      {formState.errors[inputName] && (
        <Text w="full" mt={-1} color="#E53E3E" textAlign="left" fontSize="0.78rem">
          {formState.errors[inputName]?.message as string}
        </Text>
      )}
      {listAttachmentFile && listAttachmentFile.length > 0 && (
        <VStack align="start" spacing={2} w="full">
          {listAttachmentFile.map((file, index) => (
            <HStack key={index} w="full" justify="space-between" p={2} bg={bgListFile} rounded="md" shadow="xs">
              <HStack spacing={2} minW={0} flex={1}>
                <Icon as={FiFile} color="blue.400" />
                <Stack spacing={0} gap={0}>
                  <Text fontSize="sm" noOfLines={1} maxW="200px" title={file.name}>
                    {file.name}
                  </Text>
                  <Text mt={-0.5} fontSize="0.5rem" color="gray">
                    Tamanho: {formatFileSize(file.size)}
                  </Text>
                </Stack>
              </HStack>
              <HStack spacing={1} flexShrink={0}>
                <IconButton
                  type="button"
                  aria-label={`Visualizar ${file.name}`}
                  title="Visualizar arquivo"
                  icon={<FiEye />}
                  variant="ghost"
                  size="sm"
                  colorScheme="blue"
                  onClick={() => {
                    const preview = previewUrls[index];
                    if (preview) window.open(preview, '_blank', 'noopener,noreferrer');
                  }}
                />
                <IconButton
                  type="button"
                  aria-label={`Remover ${file.name}`}
                  title="Remover arquivo"
                  icon={<FiTrash />}
                  variant="ghost"
                  size="sm"
                  colorScheme="red"
                  onClick={() => removeFile(index)}
                />
              </HStack>
            </HStack>
          ))}
        </VStack>
      )}
      {sentFiles && sentFiles.length > 0 && (
        <VStack align="start" spacing={2} w="full">
          {sentFiles.map((file, index) => (
            <HStack key={index} w="full" justify="space-between" p={2} bg="#e8fde9" rounded="md" shadow="xs">
              <HStack spacing={2}>
                <Icon as={FiFile} color="blue.400" />
                <Stack spacing={0} gap={0}>
                  <Text fontSize="sm" noOfLines={1} maxW="200px">
                    {file.name}
                  </Text>
                  <Text mt={-0.5} fontSize="0.5rem" color="gray">
                    Enviado em: {file.created_at?.br || file.created_at_patern.br}
                  </Text>
                </Stack>
              </HStack>
              <Icon
                as={FiTrash}
                color="red.400"
                cursor="pointer"
                onClick={() => onDeleteAttachment && onDeleteAttachment(file.id)}
              />
            </HStack>
          ))}
        </VStack>
      )}
    </Stack>
  );
};

export default InputAttachmentFile;
